import React, { useState, useEffect } from 'react';

interface SignupStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'current' | 'completed';
  tips?: string[];
}

export const AWSSignupWizard: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [accountInfo, setAccountInfo] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    accountName: '',
    phoneNumber: '',
    verificationCode: '',
    creditCard: {
      number: '',
      expiry: '',
      cvv: ''
    }
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  const steps: SignupStep[] = [
    {
      id: 'account-type',
      title: '🎯 Choose Account Type',
      description: 'Select Personal Account (Free Tier Available)',
      status: currentStep === 0 ? 'current' : currentStep > 0 ? 'completed' : 'pending',
      tips: [
        'Personal accounts get 12 months of free tier',
        'No charges if you stay within free tier limits',
        'SES gives 62,000 free emails per month from EC2'
      ]
    },
    {
      id: 'email-password',
      title: '📧 Email & Password',
      description: 'Create your root account credentials',
      status: currentStep === 1 ? 'current' : currentStep > 1 ? 'completed' : 'pending',
      tips: [
        'Use a real email - you\'ll need to verify it',
        'Password must be 8+ characters with mixed case, numbers, and symbols',
        'This becomes your ROOT account - keep it secure!'
      ]
    },
    {
      id: 'email-verification',
      title: '✉️ Verify Email',
      description: 'Enter the OTP sent to your email',
      status: currentStep === 2 ? 'current' : currentStep > 2 ? 'completed' : 'pending',
      tips: [
        'Check your spam folder if you don\'t see the email',
        'Code expires in 10 minutes',
        'Click "Resend" if needed'
      ]
    },
    {
      id: 'contact-info',
      title: '📱 Contact Information',
      description: 'Add phone number and address',
      status: currentStep === 3 ? 'current' : currentStep > 3 ? 'completed' : 'pending',
      tips: [
        'Phone verification via SMS or voice call',
        'Address determines your AWS region',
        'Use real info - required for billing'
      ]
    },
    {
      id: 'payment-method',
      title: '💳 Payment Method',
      description: 'Add a credit/debit card (won\'t be charged)',
      status: currentStep === 4 ? 'current' : currentStep > 4 ? 'completed' : 'pending',
      tips: [
        'AWS charges $1 for verification (refunded)',
        'No charges if you stay in free tier',
        'Set up billing alerts immediately after'
      ]
    },
    {
      id: 'support-plan',
      title: '🆘 Support Plan',
      description: 'Choose Basic (Free) Support',
      status: currentStep === 5 ? 'current' : currentStep > 5 ? 'completed' : 'pending',
      tips: [
        'Basic Support is FREE',
        'Includes documentation and forums',
        'You can upgrade later if needed'
      ]
    },
    {
      id: 'complete',
      title: '🎉 Account Created!',
      description: 'Time to secure your account',
      status: currentStep === 6 ? 'current' : currentStep > 6 ? 'completed' : 'pending',
      tips: [
        'Enable MFA immediately',
        'Create an IAM user for daily use',
        'Never use root account for regular work'
      ]
    }
  ];

  const checkPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    setPasswordStrength(strength);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const sendOTP = async () => {
    if (!validateEmail(accountInfo.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    // Send OTP via your email service
    try {
      const response = await fetch('http://localhost:8789/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: accountInfo.email,
          subject: 'Your AWS Account Verification Code',
          html: `
            <div style="font-family: Arial, sans-serif; padding: 20px;">
              <h2>AWS Account Verification</h2>
              <p>Your verification code is:</p>
              <h1 style="background: #f0f0f0; padding: 10px; text-align: center; font-size: 32px; letter-spacing: 5px;">
                ${Math.floor(100000 + Math.random() * 900000)}
              </h1>
              <p>This code expires in 10 minutes.</p>
              <p style="color: #666; font-size: 12px;">
                If you didn't request this, please ignore this email.
              </p>
            </div>
          `
        })
      });

      if (response.ok) {
        setOtpSent(true);
        setCurrentStep(2);
      }
    } catch (error) {
      console.error('Failed to send OTP:', error);
      setErrors({ otp: 'Failed to send verification code. Please try again.' });
    }
  };

  const verifyOTP = () => {
    // In real implementation, verify against stored OTP
    if (accountInfo.verificationCode.length === 6) {
      setOtpVerified(true);
      setCurrentStep(3);
    } else {
      setErrors({ verificationCode: 'Invalid code. Please check and try again.' });
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Account Type
        return (
          <div className="space-y-4">
            <div className="bg-blue-900/30 border border-blue-500 p-4 rounded-lg cursor-pointer hover:bg-blue-900/50 transition">
              <h3 className="font-bold text-lg mb-2">👤 Personal Account (Recommended)</h3>
              <ul className="text-sm space-y-1 text-blue-200">
                <li>✅ 12 months free tier included</li>
                <li>✅ Perfect for learning and side projects</li>
                <li>✅ Can upgrade to business later</li>
                <li>✅ Same features as business accounts</li>
              </ul>
              <button
                onClick={() => setCurrentStep(1)}
                className="mt-4 w-full bg-blue-500 py-2 rounded font-bold hover:bg-blue-600"
              >
                Choose Personal Account
              </button>
            </div>

            <div className="bg-gray-800/50 border border-gray-600 p-4 rounded-lg opacity-75">
              <h3 className="font-bold text-lg mb-2">🏢 Business Account</h3>
              <ul className="text-sm space-y-1 text-gray-400">
                <li>• For companies and organizations</li>
                <li>• Requires business information</li>
                <li>• Same free tier benefits</li>
                <li>• Additional compliance features</li>
              </ul>
            </div>
          </div>
        );

      case 1: // Email & Password
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email Address</label>
              <input
                type="email"
                value={accountInfo.email}
                onChange={(e) => {
                  setAccountInfo({ ...accountInfo, email: e.target.value });
                  setErrors({});
                }}
                placeholder="your.email@example.com"
                className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white"
              />
              {errors.email && (
                <p className="text-red-400 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">AWS Account Name</label>
              <input
                type="text"
                value={accountInfo.accountName}
                onChange={(e) => setAccountInfo({ ...accountInfo, accountName: e.target.value })}
                placeholder="My AWS Account"
                className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">This is just a friendly name for your account</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Root Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={accountInfo.password}
                  onChange={(e) => {
                    setAccountInfo({ ...accountInfo, password: e.target.value });
                    checkPasswordStrength(e.target.value);
                  }}
                  placeholder="StrongP@ssw0rd123!"
                  className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-2 text-gray-400 hover:text-white"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>

              {/* Password Strength Indicator */}
              <div className="mt-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={`h-2 flex-1 rounded ${
                        passwordStrength >= level
                          ? level <= 2 ? 'bg-red-500' : level <= 4 ? 'bg-yellow-500' : 'bg-green-500'
                          : 'bg-gray-700'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs mt-1 text-gray-400">
                  {passwordStrength === 0 && 'Enter a password'}
                  {passwordStrength === 1 && 'Weak - Add more characters'}
                  {passwordStrength === 2 && 'Fair - Add uppercase and numbers'}
                  {passwordStrength === 3 && 'Good - Add special characters'}
                  {passwordStrength === 4 && 'Strong password'}
                  {passwordStrength === 5 && 'Very strong password! 💪'}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Confirm Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={accountInfo.confirmPassword}
                onChange={(e) => setAccountInfo({ ...accountInfo, confirmPassword: e.target.value })}
                placeholder="Re-enter password"
                className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white"
              />
              {accountInfo.confirmPassword && accountInfo.password !== accountInfo.confirmPassword && (
                <p className="text-red-400 text-xs mt-1">Passwords don't match</p>
              )}
            </div>

            <button
              onClick={sendOTP}
              disabled={
                !accountInfo.email ||
                !accountInfo.password ||
                accountInfo.password !== accountInfo.confirmPassword ||
                passwordStrength < 3
              }
              className="w-full bg-green-500 py-2 rounded font-bold hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to Email Verification →
            </button>
          </div>
        );

      case 2: // Email Verification
        return (
          <div className="space-y-4">
            <div className="bg-yellow-900/30 border border-yellow-500 p-4 rounded">
              <p className="text-yellow-300">
                📧 We've sent a verification code to: <strong>{accountInfo.email}</strong>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Enter 6-Digit Code</label>
              <input
                type="text"
                value={accountInfo.verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setAccountInfo({ ...accountInfo, verificationCode: value });
                  setErrors({});
                }}
                placeholder="123456"
                maxLength={6}
                className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white text-center text-2xl font-mono tracking-widest"
              />
              {errors.verificationCode && (
                <p className="text-red-400 text-xs mt-1">{errors.verificationCode}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={verifyOTP}
                disabled={accountInfo.verificationCode.length !== 6}
                className="flex-1 bg-green-500 py-2 rounded font-bold hover:bg-green-600 disabled:opacity-50"
              >
                Verify Code
              </button>
              <button
                onClick={sendOTP}
                className="flex-1 bg-gray-600 py-2 rounded font-bold hover:bg-gray-700"
              >
                Resend Code
              </button>
            </div>

            <div className="text-xs text-gray-400">
              <p>Didn't receive the email? Check your spam folder or try these:</p>
              <ul className="mt-2 space-y-1">
                <li>• Make sure {accountInfo.email} is correct</li>
                <li>• Wait 1-2 minutes for delivery</li>
                <li>• Check email filters/rules</li>
              </ul>
            </div>
          </div>
        );

      case 3: // Contact Info (simulated)
        return (
          <div className="space-y-4">
            <div className="bg-blue-900/20 border border-blue-500/30 p-3 rounded">
              <p className="text-sm">AWS will open in a new tab for the remaining steps</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Phone Number</label>
              <input
                type="tel"
                value={accountInfo.phoneNumber}
                onChange={(e) => setAccountInfo({ ...accountInfo, phoneNumber: e.target.value })}
                placeholder="+1 (555) 123-4567"
                className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">AWS will send an SMS or call to verify</p>
            </div>

            <button
              onClick={() => {
                window.open('https://portal.aws.amazon.com/billing/signup', '_blank');
                setCurrentStep(4);
              }}
              className="w-full bg-orange-500 py-2 rounded font-bold hover:bg-orange-600"
            >
              Continue on AWS Website →
            </button>
          </div>
        );

      case 4: // Payment Method
        return (
          <div className="space-y-4">
            <div className="bg-green-900/30 border border-green-500 p-4 rounded">
              <h4 className="font-bold text-green-400 mb-2">💡 Important Tips:</h4>
              <ul className="text-sm space-y-1">
                <li>• AWS charges $1 USD for verification (refunded)</li>
                <li>• You won't be charged if you stay in free tier</li>
                <li>• Set up billing alerts immediately after</li>
                <li>• Free tier includes 62,000 SES emails/month</li>
              </ul>
            </div>

            <div className="text-center py-4">
              <p className="text-gray-400 mb-4">Complete these steps on AWS:</p>
              <ol className="text-left inline-block space-y-2">
                <li>✅ 1. Enter credit/debit card details</li>
                <li>⏳ 2. Complete $1 verification charge</li>
                <li>⏳ 3. Choose identity verification method</li>
              </ol>
            </div>

            <button
              onClick={() => setCurrentStep(5)}
              className="w-full bg-blue-500 py-2 rounded font-bold hover:bg-blue-600"
            >
              I've Added My Payment Method ✓
            </button>
          </div>
        );

      case 5: // Support Plan
        return (
          <div className="space-y-4">
            <div className="bg-green-900/30 border border-green-500 p-4 rounded">
              <h3 className="font-bold text-lg mb-2">✅ Choose Basic Support (FREE)</h3>
              <ul className="text-sm space-y-1">
                <li>• Access to documentation and forums</li>
                <li>• Service health dashboard</li>
                <li>• Best practices guidance</li>
                <li>• No additional cost</li>
              </ul>
            </div>

            <div className="opacity-50">
              <h4 className="font-bold mb-2">Paid Support Options:</h4>
              <ul className="text-xs space-y-1">
                <li>• Developer: $29/month - Business hours email support</li>
                <li>• Business: $100/month - 24/7 phone support</li>
                <li>• Enterprise: Custom - Technical account manager</li>
              </ul>
            </div>

            <button
              onClick={() => setCurrentStep(6)}
              className="w-full bg-green-500 py-2 rounded font-bold hover:bg-green-600"
            >
              I Selected Basic (Free) Support ✓
            </button>
          </div>
        );

      case 6: // Complete
        return (
          <div className="space-y-4">
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold mb-2">AWS Account Created!</h2>
              <p className="text-gray-400">Now let's secure it properly</p>
            </div>

            <div className="bg-red-900/30 border border-red-500 p-4 rounded">
              <h4 className="font-bold text-red-400 mb-2">🔒 Critical Security Steps:</h4>
              <ol className="text-sm space-y-2">
                <li>
                  <strong>1. Enable MFA on Root Account</strong>
                  <p className="text-xs text-gray-400">IAM → Security credentials → MFA</p>
                </li>
                <li>
                  <strong>2. Create IAM User for Daily Use</strong>
                  <p className="text-xs text-gray-400">Never use root account regularly</p>
                </li>
                <li>
                  <strong>3. Set Up Billing Alerts</strong>
                  <p className="text-xs text-gray-400">CloudWatch → Billing alarms</p>
                </li>
              </ol>
            </div>

            <div className="bg-blue-900/30 border border-blue-500 p-4 rounded">
              <h4 className="font-bold mb-2">📝 Save This Information:</h4>
              <div className="font-mono text-sm space-y-1">
                <p>Account ID: [Check in AWS Console]</p>
                <p>Sign-in URL: https://console.aws.amazon.com/</p>
                <p>Root Email: {accountInfo.email}</p>
                <p>Account Alias: {accountInfo.accountName || 'my-aws-account'}</p>
              </div>
            </div>

            <button
              onClick={() => window.location.href = '/ses-real'}
              className="w-full bg-green-500 py-3 rounded font-bold hover:bg-green-600 text-lg"
            >
              🚀 Continue to SES Setup →
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">🔧 AWS Account Setup Wizard</h1>
          <p className="text-gray-400">Let's create your free AWS account step by step</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="relative">
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-700"></div>
            <div
              className="absolute top-5 left-0 h-0.5 bg-green-500 transition-all duration-500"
              style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
            ></div>
            <div className="relative flex justify-between">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`flex flex-col items-center ${
                    index <= currentStep ? 'cursor-pointer' : ''
                  }`}
                  onClick={() => index <= currentStep && setCurrentStep(index)}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                      step.status === 'completed'
                        ? 'bg-green-600 border-green-500'
                        : step.status === 'current'
                        ? 'bg-blue-600 border-blue-500 animate-pulse'
                        : 'bg-gray-800 border-gray-700'
                    }`}
                  >
                    {step.status === 'completed' ? '✓' : index + 1}
                  </div>
                  <span className="text-xs mt-2 text-center max-w-[80px]">
                    {step.title.split(' ').slice(1).join(' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">{steps[currentStep].title}</h2>
              <p className="text-gray-400 mb-6">{steps[currentStep].description}</p>
              {renderStepContent()}
            </div>
          </div>

          {/* Tips Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 sticky top-4">
              <h3 className="font-bold mb-3">💡 Tips & Info</h3>
              <ul className="space-y-2 text-sm">
                {steps[currentStep].tips?.map((tip, index) => (
                  <li key={index} className="flex gap-2">
                    <span className="text-green-400">→</span>
                    <span className="text-gray-300">{tip}</span>
                  </li>
                ))}
              </ul>

              {currentStep === 0 && (
                <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-600/30 rounded">
                  <p className="text-xs text-yellow-300">
                    <strong>Free Tier Highlights:</strong><br />
                    • 750 hours EC2 t2.micro<br />
                    • 5 GB S3 storage<br />
                    • 62,000 SES emails/month<br />
                    • 1 million Lambda requests
                  </p>
                </div>
              )}

              {currentStep >= 4 && (
                <div className="mt-4 p-3 bg-blue-900/20 border border-blue-600/30 rounded">
                  <p className="text-xs text-blue-300">
                    <strong>Remember:</strong><br />
                    You're setting up the ROOT account. After completion, create an IAM user for daily use and never share root credentials!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};